import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Bookmark, BookmarkCheck, Check, ChevronLeft, Eye, EyeOff, Images, X } from 'lucide-react'
import { useAuthSession } from '../../lib/firebase'
import { getSections, getStationById, getStations } from '../../lib/api'
import { learnerStationLabel } from '../../lib/learnerLabels'
import type { Section, Station } from '../../lib/types'
import {
  getStationProgress,
  recordStationResult,
  setStationRevealedQuestionIds,
  toggleStationBookmark,
  toggleStationQuestionReveal,
} from '../../lib/progress'
import './study.css'

type LoadState =
  | { status: 'loading' }
  | { status: 'missing'; message: string }
  | { status: 'ready'; station: Station; stations: Station[]; sections: Section[] }

function orderedStations(stations: Station[], sections: Section[]) {
  const sectionOrder = new Map(sections.map((section) => [section.id, section.order]))
  return stations.slice().sort((a, b) =>
    (sectionOrder.get(a.sectionId) ?? 999) - (sectionOrder.get(b.sectionId) ?? 999)
    || a.order - b.order || a.id.localeCompare(b.id))
}

export function StationLearnerPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthSession()
  const uid = user?.uid ?? ''
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const [revealed, setRevealed] = useState<Set<string>>(new Set())
  const [imageIndex, setImageIndex] = useState(0)
  const [imageFailed, setImageFailed] = useState(false)
  const [progress, setProgress] = useState(() => getStationProgress(uid, id))

  useEffect(() => {
    const controller = new AbortController()
    setState({ status: 'loading' })
    setImageIndex(0)
    setImageFailed(false)
    Promise.all([getStationById(id, controller.signal), getStations({}, controller.signal), getSections(controller.signal)])
      .then(([stationResult, stationList, sectionList]) => {
        const station = stationResult.data
        if (!station) {
          setState({ status: 'missing', message: 'هذه المحطة غير موجودة أو غير متاحة حاليًا.' })
          return
        }
        const saved = getStationProgress(uid, station.id)
        const validIds = new Set(station.subquestions.map((question) => question.id))
        setProgress(saved)
        setRevealed(new Set((saved.revealedQuestionIds ?? []).filter((questionId) => validIds.has(questionId))))
        setState({
          status: 'ready', station,
          stations: stationList.data.some((item) => item.id === station.id) ? stationList.data : [...stationList.data, station],
          sections: sectionList.data,
        })
      })
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ status: 'missing', message: 'تعذر تحميل المحطة الآن. حدّث الصفحة وحاول مرة أخرى.' })
        }
      })
    return () => controller.abort()
  }, [id, uid])

  const station = state.status === 'ready' ? state.station : undefined
  const ordered = useMemo(() => state.status === 'ready' ? orderedStations(state.stations, state.sections) : [], [state])
  const currentIndex = station ? ordered.findIndex((item) => item.id === station.id) : -1
  const previous = currentIndex > 0 ? ordered[currentIndex - 1] : undefined
  const next = currentIndex >= 0 ? ordered[currentIndex + 1] : undefined
  const questions = useMemo(() => station?.subquestions.slice().sort((a, b) => a.order - b.order) ?? [], [station])
  const allRevealed = questions.length > 0 && questions.every((question) => revealed.has(question.id))
  const activeImage = station?.images[imageIndex]

  const go = useCallback((target?: Station) => {
    if (target) navigate(`/cases/${encodeURIComponent(target.id)}`)
  }, [navigate])

  const toggleQuestion = (questionId: string) => {
    if (!station) return
    const saved = toggleStationQuestionReveal(uid, station.id, questionId)
    setProgress(saved)
    setRevealed(new Set(saved.revealedQuestionIds ?? []))
  }

  const toggleAll = () => {
    if (!station || !questions.length) return
    const saved = setStationRevealedQuestionIds(uid, station.id, allRevealed ? [] : questions.map((question) => question.id))
    setProgress(saved)
    setRevealed(new Set(saved.revealedQuestionIds ?? []))
  }

  const assess = (result: 'correct' | 'wrong') => {
    if (!station) return
    const saved = recordStationResult(uid, station.id, result)
    setProgress(saved)
    if (next) window.setTimeout(() => go(next), 280)
  }

  const toggleBookmark = () => {
    if (station) setProgress(toggleStationBookmark(uid, station.id))
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement | null)?.closest('button, a, input, textarea, select')) return
      if (event.key === 'ArrowLeft') go(next)
      if (event.key === 'ArrowRight') go(previous)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, next, previous])

  if (state.status === 'loading') return <section className="case-loading" aria-busy="true">جارٍ فتح المحطة…</section>
  if (state.status === 'missing') return (
    <section className="study-empty-state" role="alert"><h1>تعذر فتح المحطة</h1><p>{state.message}</p><Link to="/">العودة إلى كل المحطات</Link></section>
  )
  if (!station) return null

  return (
    <section className={`case-player${allRevealed ? ' case-player--assessing' : ''}`} aria-labelledby="case-title">
      <header className="case-player__header">
        <div>
          <Link className="case-back" to="/"><ChevronLeft size={17} />كل المحطات</Link>
          <span className="case-counter">المحطة {currentIndex + 1} من {ordered.length}</span>
        </div>
        <button className={`case-bookmark${progress.bookmarked ? ' is-active' : ''}`} type="button" onClick={toggleBookmark} aria-pressed={progress.bookmarked}>
          {progress.bookmarked ? <BookmarkCheck size={19} /> : <Bookmark size={19} />}
          <span>{progress.bookmarked ? 'محفوظة' : 'حفظ المحطة'}</span>
        </button>
        <span className="case-player__rail" aria-hidden="true"><i style={{ width: `${((currentIndex + 1) / Math.max(ordered.length, 1)) * 100}%` }} /></span>
      </header>

      <article className="case-sheet">
        <div className="case-sheet__title">
          <span>المحطة {String(currentIndex + 1).padStart(2, '0')}</span>
          <h1 id="case-title">{learnerStationLabel(station.id, currentIndex + 1)}</h1>
        </div>

        {station.images.length > 0 && (
          <section className="case-media" aria-label="صور المحطة">
            {activeImage && !imageFailed ? (
              <figure>
                <img src={activeImage.key} alt={`صورة ${learnerStationLabel(station.id, currentIndex + 1)}`} onError={() => setImageFailed(true)} />
                {station.images.length > 1 && <figcaption><Images size={15} />صورة {imageIndex + 1} من {station.images.length}</figcaption>}
              </figure>
            ) : <div className="case-media__failed">تعذر تحميل الصورة.</div>}
            {station.images.length > 1 && (
              <div className="case-media__thumbs">
                {station.images.map((image, index) => (
                  <button key={image.id} type="button" className={index === imageIndex ? 'is-active' : ''} onClick={() => { setImageIndex(index); setImageFailed(false) }}>
                    <img src={image.key} alt={`الصورة ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {station.scenario && <p className="case-scenario">{station.scenario}</p>}

        <section className="case-questions" aria-labelledby="case-questions-title">
          <div className="case-questions__heading">
            <h2 id="case-questions-title">أسئلة المحطة</h2>
            {questions.length > 0 && <button type="button" onClick={toggleAll}>{allRevealed ? <EyeOff size={17} /> : <Eye size={17} />}{allRevealed ? 'إخفاء الإجابات' : 'كشف كل الإجابات'}</button>}
          </div>
          {questions.length === 0 ? <p className="case-no-questions">لا توجد أسئلة مضافة إلى هذه المحطة حاليًا.</p> : (
            <ol>
              {questions.map((question, index) => {
                const isOpen = revealed.has(question.id)
                return (
                  <li className="case-question" key={question.id}>
                    <div className="case-question__prompt"><span>{index + 1}</span><h3>{question.prompt}</h3></div>
                    <button className="case-question__reveal" type="button" onClick={() => toggleQuestion(question.id)} aria-expanded={isOpen}>
                      {isOpen ? <EyeOff size={16} /> : <Eye size={16} />}{isOpen ? 'إخفاء الإجابة' : 'كشف الإجابة'}
                    </button>
                    {isOpen && (
                      <div className="case-answer" role="region" aria-label={`إجابة السؤال ${index + 1}`}>
                        {question.answers.length ? (
                          <ul>{question.answers.slice().sort((a, b) => a.order - b.order).map((answer) => <li key={answer.id}>{answer.body}</li>)}</ul>
                        ) : <p>لم تُضف إجابة لهذا السؤال بعد.</p>}
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </section>

      </article>

      <div className={`case-controls-dock${allRevealed ? ' case-controls-dock--assessing' : ''}`}>
        {allRevealed && (
          <section className="case-assessment case-assessment--dock" aria-label="تقييم إجابتك">
            <div className="case-assessment__copy"><strong>قيّم حلك</strong><span>اختيارك يحفظ تقدمك</span></div>
            <div className="case-assessment__buttons">
              <button className={`case-result case-result--correct${progress.mark === 'known' ? ' is-active' : ''}`} type="button" onClick={() => assess('correct')}><Check size={19} />حليت صح</button>
              <button className={`case-result case-result--wrong${progress.mark === 'review' ? ' is-active' : ''}`} type="button" onClick={() => assess('wrong')}><X size={19} />حليت غلط</button>
            </div>
          </section>
        )}
        <footer className="case-pager">
          <button type="button" onClick={() => go(next)} disabled={!next}><span>التالي</span><ArrowLeft size={18} /></button>
          <span>{currentIndex + 1} / {ordered.length}</span>
          <button type="button" onClick={() => go(previous)} disabled={!previous}><ArrowRight size={18} /><span>السابق</span></button>
        </footer>
      </div>
    </section>
  )
}

export default function StationPage() { return <StationLearnerPage /> }
